import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { getSocket } from "@/lib/socket";

function _play(vol, tones) {
  try {
    if (vol <= 0) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, start, dur) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
    };
    tones.forEach(([f, s, d]) => play(f, s, d));
  } catch { /* audio not available */ }
}

function beep() {
  if (localStorage.getItem("metros_alert_sound") === "off") return;
  const vol = Math.max(0, Math.min(1, parseFloat(localStorage.getItem("metros_alert_volume") ?? "0.3")));
  _play(vol, [[880, 0, 0.18], [1180, 0.2, 0.22]]);
}

function chatBeep() {
  if (localStorage.getItem("metros_chat_sound") === "off") return;
  const vol = Math.max(0, Math.min(1, parseFloat(localStorage.getItem("metros_chat_volume") ?? "0.3")));
  _play(vol, [[660, 0, 0.14], [990, 0.16, 0.18]]);
}

export function AlertSoundControl() {
  const [on, setOn] = React.useState(localStorage.getItem("metros_alert_sound") !== "off");
  const [vol, setVol] = React.useState(parseFloat(localStorage.getItem("metros_alert_volume") ?? "0.3"));
  const toggle = () => { const n = !on; setOn(n); localStorage.setItem("metros_alert_sound", n ? "on" : "off"); if (n) beep(); };
  const changeVol = (v) => { setVol(v); localStorage.setItem("metros_alert_volume", String(v)); };
  return (
    <div className="rounded-xl bg-white/5 px-3 py-2.5" data-testid="alert-sound-control">
      <button onClick={toggle} data-testid="alert-sound-toggle" className="w-full flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white">
        {on ? <Volume2 size={16} className="text-metros-red" /> : <VolumeX size={16} />} Order alert sound
        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${on ? "bg-metros-red text-white" : "bg-white/10 text-gray-400"}`}>{on ? "ON" : "OFF"}</span>
      </button>
      {on && <input type="range" min="0" max="1" step="0.1" value={vol} data-testid="alert-volume"
        onChange={(e) => changeVol(parseFloat(e.target.value))} onMouseUp={() => beep()}
        className="w-full mt-2 accent-metros-red" />}
    </div>
  );
}

export function ChatSoundControl() {
  const [on, setOn] = React.useState(localStorage.getItem("metros_chat_sound") !== "off");
  const [vol, setVol] = React.useState(parseFloat(localStorage.getItem("metros_chat_volume") ?? "0.3"));
  const toggle = () => { const n = !on; setOn(n); localStorage.setItem("metros_chat_sound", n ? "on" : "off"); if (n) chatBeep(); };
  const changeVol = (v) => { setVol(v); localStorage.setItem("metros_chat_volume", String(v)); };
  return (
    <div className="rounded-xl bg-white/5 px-3 py-2.5" data-testid="chat-sound-control">
      <button onClick={toggle} data-testid="chat-sound-toggle" className="w-full flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-white">
        {on ? <Volume2 size={16} className="text-metros-red" /> : <VolumeX size={16} />} Chat alert sound
        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${on ? "bg-metros-red text-white" : "bg-white/10 text-gray-400"}`}>{on ? "ON" : "OFF"}</span>
      </button>
      {on && <input type="range" min="0" max="1" step="0.1" value={vol} data-testid="chat-volume"
        onChange={(e) => changeVol(parseFloat(e.target.value))} onMouseUp={() => chatBeep()}
        className="w-full mt-2 accent-metros-red" />}
    </div>
  );
}

export default function NewOrderAlerts() {
  const navigate = useNavigate();
  const seen = useRef(null);
  const pollRef = useRef(null);
  const chatSeen = useRef(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/orders?status=NEW");
        if (!active) return;
        const nums = data.map((o) => o.order_number);
        if (seen.current === null) { seen.current = new Set(nums); return; }
        const fresh = data.filter((o) => !seen.current.has(o.order_number));
        if (fresh.length) {
          fresh.forEach((o) => seen.current.add(o.order_number));
          beep();
          setAlerts((prev) => [...fresh.map((o) => ({ n: o.order_number, name: o.customer?.name, total: o.total, type: o.type })), ...prev].slice(0, 4));
          if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
        }
      } catch (e) { console.warn("Order alert poll failed", e); }
    };
    pollRef.current = poll;
    poll();
    const t = setInterval(poll, 12000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const s = getSocket();
    const onNew = () => pollRef.current && pollRef.current();
    s.on("new_order", onNew);
    return () => s.off("new_order", onNew);
  }, []);

  useEffect(() => {
    const s = getSocket();
    const onChat = (m) => {
      if (!m || m.sender !== "customer") return;
      chatBeep();
      if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      setAlerts((prev) => [{ n: `chat-${m.id}`, chat: true, order: m.order_number, name: m.customer_name, text: m.text }, ...prev].slice(0, 4));
    };
    s.on("chat_message", onChat);
    return () => s.off("chat_message", onChat);
  }, []);

  // Poll unread customer messages: rings + toasts (Socket.IO isn't reliable behind the proxy) and reflects count in the tab title.
  useEffect(() => {
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    const apply = (n) => { document.title = n > 0 ? `(${n}) ${baseTitle}` : baseTitle; };
    const refresh = async () => {
      try {
        const { data } = await api.get("/admin/chat/threads");
        const rows = data || [];
        const total = rows.reduce((sum, t) => sum + (t.unread || 0), 0);
        apply(total);
        if (chatSeen.current === null) { chatSeen.current = total; return; }
        if (total > chatSeen.current) {
          chatBeep();
          if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
          const t0 = rows.find((r) => (r.unread || 0) > 0 && r.last_sender === "customer");
          setAlerts((prev) => [{ n: `chat-poll-${Date.now()}`, chat: true, name: t0?.customer_name, text: t0?.last_text || "New message" }, ...prev].slice(0, 4));
        }
        chatSeen.current = total;
      } catch (e) { console.warn("Chat unread poll failed", e); }
    };
    refresh();
    const t = setInterval(refresh, 10000);
    return () => { clearInterval(t); document.title = baseTitle; };
  }, []);

  const dismiss = (n) => setAlerts((prev) => prev.filter((a) => a.n !== n));

  if (!alerts.length) return null;
  return (
    <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 w-[320px] max-w-[92vw]" data-testid="new-order-alerts">
      {alerts.map((a) => (
        <div key={a.n} className="bg-metros-red text-white rounded-2xl shadow-lg p-4 flex items-start gap-3 animate-fade-up cursor-pointer"
          onClick={() => { navigate(a.chat && !a.order ? "/admin/chat" : "/admin/orders"); dismiss(a.n); }} data-testid={`new-order-toast-${a.n}`}>
          <div className="bg-white/20 rounded-full p-2 animate-pulse"><Bell size={18} /></div>
          <div className="flex-1">
            {a.chat ? (
              <>
                <p className="font-heading font-extrabold text-sm">New message{a.order ? ` · ${a.order}` : ""}</p>
                <p className="text-xs text-white/85 line-clamp-2">{a.name || "Customer"}: {a.text}</p>
                <p className="text-[11px] font-semibold underline mt-1">Tap to reply</p>
              </>
            ) : (
              <>
                <p className="font-heading font-extrabold text-sm">New order · {a.n}</p>
                <p className="text-xs text-white/85">{a.name || "Customer"} · {a.type} · £{Number(a.total).toFixed(2)}</p>
                <p className="text-[11px] font-semibold underline mt-1">Tap to view</p>
              </>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); dismiss(a.n); }} className="text-white/80 hover:text-white"><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}
